import { AppLayout, ContentLayout } from '@amzn/awsui-components-core';
import Spinner from '@amzn/awsui-components-core/spinner';
import React, { useEffect } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { LoadingStatus } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { useAskQuick } from 'src/components/ask-quick/AskQuickContext';
import { EmbedApp } from 'src/components/das-finsuite/embedded-apps/EmbedApp';
import { EmptyState } from 'src/components/generic-components/empty-state';
import { buildEmbeddedAppPath } from 'src/utilities/urlName';

/**
 * Renders an embedded application resolved from the route. Serves the
 * human-readable `/app/:urlName` path and, as a fallback, the legacy
 * `/all-apps/:id` path (apps without a slug). Resolution prefers a `urlName`
 * match, then falls back to an `id` match so both URL shapes work.
 */
export const EmbedAppPage: React.FC = () => {
  const params = useParams<{ urlName?: string; id?: string }>();
  const routeKey = params.urlName ?? params.id;
  const [searchParams] = useSearchParams();
  const resetKey = searchParams.get('t') || '';
  const { listOfApplications, contextLoadingStatus } = useAppContext();
  const { setActiveAgentUrl } = useAskQuick();

  // Resolve the application early so the effect can reference it. Prefer the
  // urlName slug, then fall back to the id-based lookup.
  const application =
    contextLoadingStatus === LoadingStatus.Completed
      ? listOfApplications.find((app) => app.urlName === routeKey) ?? listOfApplications.find((app) => app.id === routeKey)
      : undefined;
  const agentUrl = application?.qChatConfig?.isEnabled ? application.qChatConfig.agentUrl : null;

  // When the embedded app has a qChatConfig with an agent URL, surface it
  // in the QuickSuite panel. Reset back to the default agent on unmount.
  useEffect(() => {
    if (agentUrl) {
      setActiveAgentUrl(agentUrl);
    }
    return () => setActiveAgentUrl(null);
  }, [agentUrl, setActiveAgentUrl]);

  if (contextLoadingStatus !== LoadingStatus.Completed) {
    return <Spinner size="large" />;
  }

  if (!application) {
    return <EmptyState title="Application Not Found" description="The application you are looking for does not exist." verticalCenter={true} />;
  }

  if (!application.embeddingEnabled) {
    return <Navigate to="/all-apps" replace />;
  }

  return (
    <AppLayout
      contentType="dashboard"
      disableContentPaddings
      navigationHide
      toolsHide
      maxContentWidth={Number.MAX_VALUE}
      content={
        <ContentLayout disableOverlap>
          <EmbedApp key={`${application.id}-${resetKey}`} application={application} />
        </ContentLayout>
      }
    />
  );
};

/**
 * Legacy `/all-apps/:id` handler. Redirects (replace) to the human-readable
 * `/app/:urlName` path when the app has a slug, preserving any query string;
 * otherwise it renders the embed in place, since apps without a urlName
 * permanently keep their id-based URL.
 */
export const EmbedAppLegacyRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const { listOfApplications, contextLoadingStatus } = useAppContext();

  if (contextLoadingStatus !== LoadingStatus.Completed) {
    return <Spinner size="large" />;
  }

  const application = listOfApplications.find((app) => app.id === id);
  if (application?.urlName) {
    return <Navigate to={`${buildEmbeddedAppPath(application)}${search}`} replace />;
  }

  // Unknown id or no slug → render the embed by id (EmbedAppPage resolves params.id).
  return <EmbedAppPage />;
};
